<!--
 * Path: src/core/components/floatingPanel/FloatingPanelMain.vue
 * Task: Render the draggable, resizable floating panel with custom high-contrast HUD properties from the centralized Vuetify theme.
-->
<template>
  <div
    v-if="modelValue"
    ref="panelRef"
    class="floating-panel elevation-12 rounded-lg d-flex flex-column"
    :style="panelStyle"
  >
    <!-- Drag Handle / Header -->
    <div
      class="panel-header d-flex align-center justify-space-between px-4 py-2 flex-shrink-0"
      :class="{ 'cursor-move': !isResizing }"
      @mousedown="startDrag"
      @touchstart.passive="startDrag"
    >
      <!-- Title text color explicitly bound to the central HUD text variable -->
      <span class="text-subtitle-2 font-weight-bold" style="color: rgb(var(--v-theme-hud-text)) !important;">{{ title }}</span>
      <v-btn
        v-if="showCloseButton"
        icon
        variant="text"
        density="comfortable"
        size="small"
        style="color: rgb(var(--v-theme-hud-text-secondary)) !important;"
        @click="closePanel"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-divider class="flex-shrink-0" style="border-color: rgba(var(--v-theme-hud-border), 0.15) !important;"></v-divider>

    <!-- Menu Bar Slot -->
    <FloatingPanelToolbar v-if="$slots.menu" class="flex-shrink-0">
      <slot name="menu"></slot>
    </FloatingPanelToolbar>

    <!-- Scrollable Content Area with body text color bound to hud-text-secondary -->
    <FloatingPanelList class="flex-grow-1 min-h-0 overflow-y-auto" style="color: rgb(var(--v-theme-hud-text-secondary)) !important;">
      <slot></slot>
    </FloatingPanelList>

    <!-- Footer Slot -->
    <FloatingPanelFooter
      v-slot:default
      v-if="$slots.footer || itemCount !== undefined"
      :item-count="itemCount"
      class="flex-shrink-0"
    >
      <slot name="footer"></slot>
    </FloatingPanelFooter>

    <!-- Vertical Resize Handle at the bottom edge -->
    <div
      v-if="resizable"
      class="resize-handle-bottom"
      @mousedown.prevent="startResize"
      @touchstart.passive="startResize"
    ></div>
  </div>
</template>

<script setup lang="ts">
/**
 * Path: src/core/components/floatingPanel/FloatingPanelMain.vue
 * Task: Setup drag margins, sizing boundaries, and dynamic high-contrast HUD properties computed from active Vuetify themes.
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import FloatingPanelToolbar from './FloatingPanelToolbar.vue';
import FloatingPanelList from './FloatingPanelList.vue';
import FloatingPanelFooter from './FloatingPanelFooter.vue';

interface Props {
  title?: string;
  width?: string;
  height?: string;
  itemCount?: number;
  resizable?: boolean;
  showCloseButton?: boolean;
  initialPosX?: number;
  initialPosY?: number;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Panel',
  width: '340px',
  height: '480px',
  itemCount: undefined,
  resizable: true,
  showCloseButton: true,
  initialPosX: 20,
  initialPosY: 64
});

const modelValue = defineModel<boolean>({ default: false });

const panelRef = ref<HTMLElement | null>(null);
const posX = ref<number>(props.initialPosX);
const posY = ref<number>(props.initialPosY);
const isDragging = ref<boolean>(false);

let startX: number = 0;
let startY: number = 0;

// Reactive state for vertical resizing
const panelHeight = ref<number | 'auto'>(props.height === 'auto' ? 'auto' : (parseInt(props.height, 10) || 480));

watch(() => props.height, (newVal) => {
  panelHeight.value = newVal === 'auto' ? 'auto' : (parseInt(newVal, 10) || 480);
});

const panelStyle = computed(() => {
  const hStyle = props.resizable
    ? (panelHeight.value === 'auto' ? 'auto' : `${panelHeight.value}px`)
    : props.height;
  return {
    right: `${posX.value}px`,
    top: `${posY.value}px`,
    width: props.width,
    height: hStyle,
    position: 'absolute' as const,
    zIndex: 150,
    backgroundColor: 'rgba(var(--v-theme-hud-background), 0.88)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(var(--v-theme-hud-border), 0.15)',
    color: 'rgb(var(--v-theme-hud-text))'
  };
});

// --- Strict Type Guard for Touch Events ---
const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent => {
  return 'touches' in e;
};

const startDrag = (e: MouseEvent | TouchEvent): void => {
  if (isResizing.value) return; // Block dragging if resizing is active
  isDragging.value = true;

  const clientX = isTouchEvent(e) ? (e.touches[0]?.clientX ?? 0) : e.clientX;
  const clientY = isTouchEvent(e) ? (e.touches[0]?.clientY ?? 0) : e.clientY;

  const parent = panelRef.value ? (panelRef.value.offsetParent as HTMLElement) : null;
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();
  const localMouseX = clientX - parentRect.left;
  const localMouseRight = parentRect.width - localMouseX;
  const localMouseY = clientY - parentRect.top;

  startX = localMouseRight - posX.value;
  startY = localMouseY - posY.value;

  document.body.style.cursor = 'move';
  document.body.style.userSelect = 'none';

  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
  window.addEventListener('touchmove', onDrag, { passive: false });
  window.addEventListener('touchend', stopDrag);
};

const onDrag = (e: MouseEvent | TouchEvent): void => {
  if (!isDragging.value) return;
  if (e.cancelable) e.preventDefault();

  const clientX = isTouchEvent(e) ? (e.touches[0]?.clientX ?? 0) : e.clientX;
  const clientY = isTouchEvent(e) ? (e.touches[0]?.clientY ?? 0) : e.clientY;

  const parent = panelRef.value ? (panelRef.value.offsetParent as HTMLElement) : null;
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();
  const localMouseX = clientX - parentRect.left;
  const localMouseRight = parentRect.width - localMouseX;
  const localMouseY = clientY - parentRect.top;

  let newX = localMouseRight - startX;
  let newY = localMouseY - startY;

  const panelWidth = panelRef.value ? panelRef.value.offsetWidth : parseInt(props.width, 10);
  const panelHeightVal = panelRef.value ? panelRef.value.offsetHeight : (panelHeight.value === 'auto' ? 180 : (panelHeight.value as number));

  // Bound within parent container limits
  newX = Math.max(10, Math.min(parentRect.width - panelWidth - 10, newX));
  newY = Math.max(10, Math.min(parentRect.height - panelHeightVal - 10, newY));

  posX.value = newX;
  posY.value = newY;
};

const stopDrag = (): void => {
  isDragging.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';

  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('touchmove', onDrag);
  window.removeEventListener('touchend', stopDrag);
};

// Vertical Resizing Logic
const isResizing = ref<boolean>(false);
let startYResize: number = 0;
let startHeight: number = 0;

const startResize = (e: MouseEvent | TouchEvent): void => {
  isResizing.value = true;

  startYResize = isTouchEvent(e) ? (e.touches[0]?.clientY ?? 0) : e.clientY;
  startHeight = panelHeight.value === 'auto' ? (panelRef.value?.offsetHeight || 180) : (panelHeight.value as number);

  document.body.style.cursor = 'ns-resize';
  document.body.style.userSelect = 'none';

  window.addEventListener('mousemove', onResize);
  window.addEventListener('mouseup', stopResize);
  window.addEventListener('touchmove', onResize, { passive: false });
  window.addEventListener('touchend', stopResize);
};

const onResize = (e: MouseEvent | TouchEvent): void => {
  if (!isResizing.value) return;
  if (e.cancelable) e.preventDefault();

  const clientY = isTouchEvent(e) ? (e.touches[0]?.clientY ?? 0) : e.clientY;
  const deltaY = clientY - startYResize;
  let newHeight = startHeight + deltaY;

  // Resizing boundaries enforce
  const parent = panelRef.value ? (panelRef.value.offsetParent as HTMLElement) : null;
  const maxHeight = parent ? parent.clientHeight - posY.value - 20 : 800;
  newHeight = Math.max(100, Math.min(maxHeight, newHeight));

  panelHeight.value = newHeight;
};

const stopResize = (): void => {
  isResizing.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';

  window.removeEventListener('mousemove', onResize);
  window.removeEventListener('mouseup', stopResize);
  window.removeEventListener('touchmove', onResize);
  window.removeEventListener('touchend', stopResize);
};

const closePanel = (): void => {
  modelValue.value = false;
};

const handleResize = (): void => {
  if (!modelValue.value) return;
  const parent = panelRef.value ? (panelRef.value.offsetParent as HTMLElement) : null;
  if (!parent) return;

  const parentWidth = parent.offsetWidth;
  const parentHeight = parent.offsetHeight;
  const panelWidth = panelRef.value ? panelRef.value.offsetWidth : parseInt(props.width, 10);
  const panelHeightVal = panelRef.value ? panelRef.value.offsetHeight : (panelHeight.value === 'auto' ? 180 : (panelHeight.value as number));

  posX.value = Math.max(10, Math.min(parentWidth - panelWidth - 10, posX.value));
  posY.value = Math.max(10, Math.min(parentHeight - panelHeightVal - 10, posY.value));
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.floating-panel {
  transition: box-shadow 0.2s ease;
  overflow: hidden;
}

.panel-header {
  user-select: none;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  background-color: rgba(var(--v-theme-on-surface), 0.03);
}

.cursor-move {
  cursor: move;
}

.resize-handle-bottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 8px;
  cursor: ns-resize;
  background: transparent;
  z-index: 160;
}

.resize-handle-bottom:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
}
</style>
